import { jsx as _jsx } from "react/jsx-runtime";
import { getIconSizePx, getIconStrokeWidthPx, } from '#pplx/frontend/aether/components/Icon/getIconSizePx.js';
import { IconPrimitive } from '#pplx/frontend/aether/primitives/IconPrimitive/index.js';
import { memo } from 'react';
/**
 * Pixel size the `size` prop resolves to. For footprint-matching placeholders
 * (e.g. reserving an icon's space while its data loads) so late-resolving
 * icons don't reflow surrounding layout.
 */
const ICON_COLOR_CLASSES = {
    default: 'text-primary',
    defaultInverted: 'text-inverse',
    light: 'text-secondary',
    ultraLight: 'text-tertiary',
    caution: 'text-negative-primary',
    super: 'text-super-primary',
    white: 'text-white',
    positive: 'text-positive-primary',
    negative: 'text-negative-primary',
    warning: 'text-warning-primary',
    max: 'text-super-primary',
    'finance-positive': 'text-finance-positive-primary',
    'finance-negative': 'text-finance-negative-primary',
};
/**
 * Unified icon component supporting Tabler Icons, custom icon sprite definitions, and fetched
 * icons-as-data symbol definitions with consistent sizing.
 *
 * @category components
 * @keywords icon, components, unified, component, supporting, both, tabler, icons, custom, sprite,
 *   symbol, icons-as-data, definitions, with, consistent, sizing, size, aria-label, aria-hidden,
 *   decorative, button, banner, chip, menu, empty-state
 * @whenToUse Use Icon as the standard wrapper for rendering icons throughout the design system. It
 *   abstracts over both Tabler icon components and sprite-based icons with a unified size API. Use
 *   it inside buttons, banners, chips, menus, and other components whenever an icon is needed,
 *   ensuring consistent sizing and rendering behavior across the application.
 * @guidelineDo
 *   - Always use Icon as the default choice for rendering icons — it is the core component that
 *     provides a unified size API and handles both Tabler and sprite-based icons. Prefer Icon over
 *     IconPrimitive in all standard cases
 *   - Use the size prop (tiny, small, medium, default, large, xlarge) to match the icon scale to its
 *     surrounding context
 *   - Pass an IconSpriteDef string for optimized sprite-based icons, a TablerIconProp for Tabler
 *     icon components, or a fetched IconSymbolDef entry when rendering backend-managed
 *     icons-as-data symbol payloads
 *   - Use the default size for standalone icons and smaller sizes (tiny, small) when pairing with
 *     compact UI elements like chips or badges
 *   - Prefer sprite-based icons via $icon() for better rendering performance when available
 *   - Use the aether Icon component with $icon('name') for all icon usage in product code — never
 *     import IconPrimitive or IconSprite directly.
 *   - Use the Icon component's size prop for consistent sizing rather than setting width/height
 *     manually.
 * @guidelineDont
 *   - Don't reach for IconPrimitive when Icon can do the job — Icon provides consistent sizing and
 *     rendering for all icon types. Only use IconPrimitive as a last resort when building a new
 *     Aether core component
 *   - Don't apply custom sizing via className — use the size prop which maps to consistent design
 *     token sizes
 *   - Don't leave a standalone meaningful Icon unlabeled — an unlabeled Icon is decorative and
 *     hidden from assistive technology, so give it an aria-label (or label the parent control) when
 *     it conveys meaning on its own
 *   - Don't mix icon sources inconsistently — prefer one icon system (sprite or Tabler) within a
 *     given UI region for visual consistency
 *   - Don't pass raw SVG elements or hand-authored symbol markup — Icon expects a TablerIconProp,
 *     an IconSpriteDef string, or a fetched IconSymbolDef from the icons-as-data endpoints
 *   - Don't import IconPrimitive or IconSprite in product code — these are internal implementation
 *     details. Use aether/Icon instead.
 *   - Don't set icon color via inline styles — use CSS classes (e.g., text-secondary) or the Icon
 *     component's color prop to avoid specificity conflicts.
 * @accessibility
 *   - Icon is decorative by default: an unlabeled Icon resolves to aria-hidden so it is not
 *     announced as an unnamed image. Pass aria-label to keep a standalone meaningful icon
 *     discoverable, and label the parent Button or Link when the icon is inside a control
 *   - When used inside interactive elements (buttons, links), the icon stays hidden and the parent
 *     control carries the accessible name — no additional ARIA attributes are needed on the icon
 *     itself
 *   - Pass aria-hidden={false} as an escape hatch to force-expose an icon to assistive technology;
 *     in that case you must also pass an aria-label so it has an accessible name. An explicit
 *     aria-hidden={true} can force-hide even a labeled icon
 *   - Size mappings (tiny=xs, small=sm, medium=md, default=base, large=lg, xlarge=2xl) ensure
 *     consistent visual weight across the design system; xlarge renders at 32px with a 1.8px
 *     stroke width for stroked icons
 * @relatedComponents
 *   - button
 *   - banner
 *   - chip
 *   - menu
 *   - empty-state
 */
export const Icon = memo(function Icon({ icon, size = 'default', color, 'aria-label': ariaLabel, 'aria-hidden': ariaHidden, }) {
    // Runtime safeguard for when an icon is not found in a registry so
    // lookup returns undefined but it may be casted `as` IconSpriteDef.
    if (!icon) {
        return null;
    }
    const numericSize = getIconSizePx(size);
    const strokeWidth = getIconStrokeWidthPx(size);
    const colorClass = color ? ICON_COLOR_CLASSES[color] : undefined;
    // Decorative-by-default: hide unlabeled icons from the accessibility tree.
    // An explicit `aria-hidden` (including `false`) always wins; a labeled icon
    // stays discoverable. IconPrimitive keeps its explicit, no-default behavior.
    const resolvedAriaHidden = ariaHidden ?? (ariaLabel ? undefined : true);
    if (typeof icon === 'string') {
        return (_jsx(IconPrimitive, { icon: icon, size: numericSize, strokeWidth: strokeWidth, className: colorClass, "aria-label": ariaLabel, "aria-hidden": resolvedAriaHidden }));
    }
    if (typeof icon === 'object') {
        return (_jsx(IconPrimitive, { type: "symbol", icon: icon, size: numericSize, strokeWidth: strokeWidth, className: colorClass, "aria-label": ariaLabel, "aria-hidden": resolvedAriaHidden }));
    }
    return (_jsx(IconPrimitive, { type: "component", icon: icon, size: numericSize, strokeWidth: strokeWidth, className: colorClass, "aria-label": ariaLabel, "aria-hidden": resolvedAriaHidden }));
});
//# sourceMappingURL=index.js.map